const REPORT_MEDIA_BASE_URL = "https://dreamapp.b-cdn.net";

const REPORT_TYPE_DETAILS = {
  user: {
    reportType: "user",
    title: "Reported User Accounts",
    eyebrow: "Users Moderation",
    description:
      "Track reports filed against user accounts, review evidence, and spot repeat reporters quickly.",
    helper:
      "Backend search filters by reporter username. Use the local filters for reason, evidence, and sorting.",
    section: "Users",
    targetLabel: "Account ID",
    theme: {
      heroStart: "#0f766e",
      heroEnd: "#1d4ed8",
      accent: "#0f766e",
      accentSoft: "#d9f7f2",
      accentText: "#0f766e",
      chipBg: "#e6fffb",
      chipText: "#115e59",
    },
  },
  profile: {
    reportType: "profile",
    title: "Reported Profiles",
    eyebrow: "Users Moderation",
    description:
      "Review profile-specific reports to catch offensive bios, profile media, and profile-level abuse.",
    helper:
      "Profile reports are grouped under Users because they target a member's public profile presentation.",
    section: "Users",
    targetLabel: "Profile ID",
    theme: {
      heroStart: "#9a3412",
      heroEnd: "#ea580c",
      accent: "#c2410c",
      accentSoft: "#ffedd5",
      accentText: "#9a3412",
      chipBg: "#fff1e6",
      chipText: "#9a3412",
    },
  },
  video: {
    reportType: "video",
    title: "Reported Videos",
    eyebrow: "Videos Moderation",
    description:
      "Moderate reported video content with reporter context, evidence previews, and fast queue insights.",
    helper:
      "This screen is focused on video-content reports only, while search still uses the reporter username.",
    section: "Videos",
    targetLabel: "Video ID",
    theme: {
      heroStart: "#0f172a",
      heroEnd: "#0284c7",
      accent: "#0369a1",
      accentSoft: "#dbeafe",
      accentText: "#0f3d6b",
      chipBg: "#e0f2fe",
      chipText: "#0c4a6e",
    },
  },
  post_comment: {
    reportType: "post_comment",
    title: "Reported Video Comments",
    eyebrow: "Videos Moderation",
    description:
      "Review reports filed against video comments so moderators can identify abusive or spammy discussion quickly.",
    helper:
      "Comment reports sit in Videos because these comments belong to video threads.",
    section: "Videos",
    targetLabel: "Comment ID",
    theme: {
      heroStart: "#155e75",
      heroEnd: "#0f766e",
      accent: "#0f766e",
      accentSoft: "#d7f7ec",
      accentText: "#115e59",
      chipBg: "#def7f3",
      chipText: "#115e59",
    },
  },
  post_comment_reply: {
    reportType: "post_comment_reply",
    title: "Reported Comment Replies",
    eyebrow: "Videos Moderation",
    description:
      "Moderate replies on video comments with evidence summaries and quick reason breakdowns.",
    helper:
      "Reply reports are separated from top-level comments so the moderation queue stays easier to scan.",
    section: "Videos",
    targetLabel: "Reply ID",
    theme: {
      heroStart: "#7c2d12",
      heroEnd: "#b45309",
      accent: "#a16207",
      accentSoft: "#fef3c7",
      accentText: "#92400e",
      chipBg: "#fff5da",
      chipText: "#92400e",
    },
  },
  post: {
    reportType: "post",
    title: "Reported Feed Posts",
    eyebrow: "Feed Moderation",
    description:
      "Monitor standard feed-post reports, review reporter context, and inspect attached evidence without leaving the queue.",
    helper:
      "These are feed post reports, so they live alongside the rest of the Feed moderation tooling.",
    section: "Feed",
    targetLabel: "Post ID",
    theme: {
      heroStart: "#14532d",
      heroEnd: "#0f766e",
      accent: "#15803d",
      accentSoft: "#dcfce7",
      accentText: "#166534",
      chipBg: "#e9fbe9",
      chipText: "#166534",
    },
  },
  picture_post: {
    reportType: "picture_post",
    title: "Reported Picture Posts",
    eyebrow: "Feed Moderation",
    description:
      "Review image-based feed post reports with evidence previews and local queue filters for faster moderation.",
    helper:
      "Picture-post reports are split from standard posts so image-heavy feed abuse is easier to manage.",
    section: "Feed",
    targetLabel: "Picture Post ID",
    theme: {
      heroStart: "#b45309",
      heroEnd: "#f97316",
      accent: "#c2410c",
      accentSoft: "#ffedd5",
      accentText: "#9a3412",
      chipBg: "#fff1e6",
      chipText: "#9a3412",
    },
  },
  picture_post_commen: {
    reportType: "picture_post_commen",
    title: "Reported Picture Post Comments",
    eyebrow: "Feed Moderation",
    description:
      "Keep picture-post comment moderation separate from post queues so comment issues can be handled faster.",
    helper:
      "The API uses `picture_post_commen`, and this screen presents it as picture post comments for admins.",
    section: "Feed",
    targetLabel: "Comment ID",
    theme: {
      heroStart: "#1d4ed8",
      heroEnd: "#0891b2",
      accent: "#0f5fa8",
      accentSoft: "#dbeafe",
      accentText: "#1e40af",
      chipBg: "#e7f1ff",
      chipText: "#1d4ed8",
    },
  },
};

const formatLabel = (value) => {
  if (!value) {
    return "N/A";
  }

  return value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const getReportTypeDetail = (reportType) =>
  REPORT_TYPE_DETAILS[reportType] || {
    reportType,
    title: formatLabel(reportType),
    eyebrow: "Reports",
    description: "Review this report queue.",
    helper: "Search filters by reporter username.",
    section: "Reports",
    targetLabel: "Target ID",
    theme: {
      heroStart: "#12345a",
      heroEnd: "#1e5eff",
      accent: "#1e5eff",
      accentSoft: "#dce8ff",
      accentText: "#1e5eff",
      chipBg: "#edf3ff",
      chipText: "#2048b7",
    },
  };

const resolveMediaUrl = (value) => {
  if (!value) {
    return "";
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `${REPORT_MEDIA_BASE_URL}/${value.replace(/^\/+/, "")}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleString();
};

const formatShortDate = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatRelativeTime = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  const diffInSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const divisions = [
    { amount: 60, unit: "second" },
    { amount: 60, unit: "minute" },
    { amount: 24, unit: "hour" },
    { amount: 7, unit: "day" },
    { amount: 4.34524, unit: "week" },
    { amount: 12, unit: "month" },
    { amount: Number.POSITIVE_INFINITY, unit: "year" },
  ];

  let duration = diffInSeconds;

  for (let index = 0; index < divisions.length; index += 1) {
    const division = divisions[index];

    if (Math.abs(duration) < division.amount) {
      if (typeof Intl !== "undefined" && Intl.RelativeTimeFormat) {
        const formatter = new Intl.RelativeTimeFormat(undefined, {
          numeric: "auto",
        });

        return formatter.format(Math.round(duration), division.unit);
      }

      return formatDateTime(value);
    }

    duration /= division.amount;
  }

  return formatDateTime(value);
};

export {
  REPORT_MEDIA_BASE_URL,
  REPORT_TYPE_DETAILS,
  formatDateTime,
  formatLabel,
  formatRelativeTime,
  formatShortDate,
  getReportTypeDetail,
  resolveMediaUrl,
};
